import { useState } from 'react'

import { ArticleFlat, AlmanacFile as File } from 'types'
import { FolderClient } from 'api/clients/FolderClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import Styles from './DeleteModal.module.scss'
import { Modal, useModals } from 'almanac-ui/Modal'
import { optimisticRemoveFilesFromInfiniteLists } from 'hooks/queries/files/cacheUtils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'

export interface DeleteModalProps {
  files?: Pick<File, 'title' | 'id' | 'type'>[]
  onRequest: () => void
  onFail?: () => void
  onOptimisticDelete?: (fileIds: string[]) => void
  article?: ArticleFlat | null
  archivedOnDelete?: boolean
}

// The 'article' param should be passed if the doc is being deleted from the editor. If the file(s) are
// being deleted form the All Files page, then the file(s) should be received through the 'files' param
const DeleteModal = ({
  files = [],
  onRequest,
  article = null,
  onOptimisticDelete,
  onFail,
  archivedOnDelete = false,
}: DeleteModalProps) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { closeModal } = useModals()
  const [submitting, setSubmitting] = useState(false)
  let fileNames: string

  if (!!article) {
    // If deleting doc from editor
    fileNames = article.title
  } else {
    // If deleting docs from All Files
    fileNames = files.length === 1 ? files[0].title : `these ${files.length} items`
  }

  const deleteFiles = async () => {
    if (submitting) return

    // If deleting through editor, the 'onRequest' param will take care of the deletion
    if (!!article) {
      setSubmitting(true)
      onRequest()

      return
    }

    const ids = files.map((file) => file.id)
    onOptimisticDelete?.(ids)
    optimisticRemoveFilesFromInfiniteLists(queryClient, workspaceId, ids)

    const folderIds: string[] = []
    const articleIds: string[] = []
    const almanacFileIds: string[] = []

    files.forEach((file) => {
      if (file.type === 'folder') folderIds.push(file.id)
      if (file.type === 'article' || file.type === 'handbook') articleIds.push(file.id)
      if (['media_file', 'design', 'video'].includes(file.type)) almanacFileIds.push(file.id)
    })

    setSubmitting(true)
    const res = await FolderClient.bulkDestroyFiles(folderIds, articleIds, almanacFileIds)

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      closeModal()
      showToastMessage(
        <span>
          🎉 <b>{fileNames}</b> {files.length > 1 ? 'have' : 'has'} been deleted!
        </span>,
        'success',
      )
      onRequest()
    } else {
      onFail?.()
    }

    setSubmitting(false)
  }
  let fileText = 'files'
  if (files.length === 1) {
    fileText = 'doc'
    if (files[0].type === 'folder') fileText = 'project'
    if (files[0].type === 'design') fileText = 'design'
    if (files[0].type === 'video') fileText = 'video'
  }

  const deletionText = `${files.length > 1 ? 'These' : 'This'} ${files.length > 1 ? 'files' : fileText}`

  return (
    <Modal.Root size="sm">
      <Modal.Header title={`Delete ${fileNames}?`} />
      <Modal.Content>
        <div>
          {archivedOnDelete ? (
            <>
              <span>{deletionText}</span> will be moved to Archive Posts and permanently deleted after 30 days. Are you sure
              you want to delete <span className={Styles.bold}>{fileNames}</span>?
            </>
          ) : (
            <>
              <span>{deletionText}</span> will be deleted forever, and all users will lose access. Are you sure you want
              to delete <span className={Styles.bold}>{fileNames}</span>?
            </>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton onPress={closeModal} size="lg" variant="tertiary">
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton onPress={deleteFiles} size="lg">
            {submitting ? 'Deleting...' : 'Delete'}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}

export default DeleteModal
